import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    BarChart3,
    CreditCard,
    FileText,
    LogOut,
    Moon,
    PanelLeftClose,
    PanelLeftOpen,
    Sun,
    TrendingUp,
    User,
    Wallet,
    type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../contexts/AuthContext';
import { useColorMode } from '../theme/ColorModeSelect';
import LanguageSelectorDropdown from '../theme/LanguageSelectorDropdown';
import { useTraderPermissions } from '../hooks/useTraderPermissions';
import { useDocumentDirection } from '@/hooks/useDocumentDirection';
import { ModulePermission, PermissionAction } from '../types/permissions';
import { useSystemConfiguration } from '../api/system-configuration/use-system-configuration';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import BrandMark from '@/layouts/crm/BrandMark';
import { cn } from '@/lib/utils';

interface SidebarItem {
    icon: LucideIcon;
    labelKey: 'trading' | 'accounts' | 'orders' | 'trades' | 'transactions' | 'account';
    path: string;
    permission?: { module: ModulePermission; action: PermissionAction };
}

const SIDEBAR_ITEMS: SidebarItem[] = [
    { icon: TrendingUp, labelKey: 'trading', path: '/trade' },
    { icon: Wallet, labelKey: 'accounts', path: '/' },
    {
        icon: FileText,
        labelKey: 'orders',
        path: '/orders',
        permission: { module: ModulePermission.ORDERS, action: PermissionAction.VIEW },
    },
    {
        icon: BarChart3,
        labelKey: 'trades',
        path: '/trades',
        permission: { module: ModulePermission.TRADES, action: PermissionAction.VIEW },
    },
    {
        icon: CreditCard,
        labelKey: 'transactions',
        path: '/transactions',
        permission: { module: ModulePermission.TRANSACTIONS, action: PermissionAction.VIEW },
    },
    { icon: User, labelKey: 'account', path: '/profile' },
];

// Language trigger styled as a sidebar row (the shared control defaults to
// 48px icon / pill sizes).
const languageTriggerBase =
    'text-muted-foreground hover:bg-surface-hover hover:text-foreground [&_svg]:size-4';

const focusRing = 'outline-none focus-visible:ring-2 focus-visible:ring-ring';
// Same row anatomy as the CRM sidebar (src/layouts/crm/MenuContent.tsx).
const rowBase = cn(
    'relative flex items-center gap-2.5 rounded-md text-[13px] font-medium no-underline transition-colors duration-100',
    focusRing
);
const rowIdle = 'text-muted-foreground hover:bg-surface-hover hover:text-foreground';
const rowActive = 'bg-primary/10 text-primary';

/**
 * Trader desktop sidebar: a floating glass card inset from the viewport edge,
 * the tenant logo above it, collapsible to an icon rail. Colours come from the
 * tenant's brand (active row, logo tile), like the CRM shell.
 */
const TradingSidebar: React.FC<{
    collapsed: boolean;
    canToggle: boolean;
    onToggleCollapsed: () => void;
}> = ({ collapsed, canToggle, onToggleCollapsed }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation('sidebar');
    const { t: tCrm } = useTranslation('crm');
    const { logout } = useAuth();
    const { mode, setMode } = useColorMode();
    const { hasPermission } = useTraderPermissions();
    const { data: systemConfig } = useSystemConfiguration();
    const dir = useDocumentDirection();
    const tooltipSide = dir === 'rtl' ? 'left' : 'right';

    const logoUrl = systemConfig?.system_logo_url || systemConfig?.logo_url;
    const systemName = systemConfig?.system_name;

    const items = SIDEBAR_ITEMS.filter(
        item => !item.permission || hasPermission(item.permission.module, item.permission.action)
    );
    const isActive = (path: string) => location.pathname === path;
    const isDark = mode === 'dark';
    const languageTriggerClass = cn(
        languageTriggerBase,
        collapsed
            ? 'mx-auto h-9 w-9'
            : 'h-9 w-full justify-start gap-0 px-2.5 text-[13px] font-medium [&_svg]:me-2.5'
    );

    /** One sidebar row; icon-only with a tooltip when the rail is collapsed. */
    const renderRow = (
        key: string,
        icon: LucideIcon,
        label: string,
        action: { to: string } | { onClick: () => void },
        active = false
    ) => {
        const Icon = icon;
        const content = (
            <>
                <Icon aria-hidden="true" className="size-4 shrink-0" />
                {!collapsed && (
                    <span className="min-w-0 flex-grow truncate text-start">{label}</span>
                )}
            </>
        );
        const className = cn(
            rowBase,
            collapsed ? 'mx-auto h-9 w-9 justify-center' : 'h-9 w-full px-2.5',
            active ? rowActive : rowIdle
        );
        const control =
            'to' in action ? (
                <Link
                    to={action.to}
                    aria-label={collapsed ? label : undefined}
                    aria-current={active ? 'page' : undefined}
                    className={className}
                >
                    {content}
                </Link>
            ) : (
                <button
                    type="button"
                    onClick={action.onClick}
                    aria-label={collapsed ? label : undefined}
                    className={className}
                >
                    {content}
                </button>
            );

        if (!collapsed) return <li key={key}>{control}</li>;
        return (
            <li key={key}>
                <Tooltip>
                    <TooltipTrigger asChild>{control}</TooltipTrigger>
                    <TooltipContent side={tooltipSide} className="text-xs">
                        {label}
                    </TooltipContent>
                </Tooltip>
            </li>
        );
    };

    const expandCollapseLabel = collapsed
        ? tCrm('chrome.expandSidebar')
        : tCrm('chrome.collapseSidebar');
    const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;

    return (
        <TooltipProvider delayDuration={200}>
            <div
                className={cn(
                    'flex h-full shrink-0 flex-col ps-2 pt-2 transition-[width] duration-200 ease-out',
                    collapsed ? 'w-[var(--sidebar-w-collapsed)]' : 'w-[var(--sidebar-w)]'
                )}
            >
                <aside
                    aria-label={t('trading')}
                    className="mb-2 flex min-h-0 flex-1 flex-col overflow-hidden rounded-[calc(var(--radius)+6px)] border border-glass-nav-border bg-glass-nav backdrop-blur-xl backdrop-saturate-150"
                >
                    {/* Brand row: the trader shell has no top bar. */}
                    <div className="relative flex h-14 shrink-0 items-center justify-center border-b border-border-subtle px-2">
                        <Link
                            to="/"
                            aria-label={systemName || t('quantix')}
                            className={cn('flex items-center rounded-md no-underline', focusRing)}
                        >
                            <BrandMark logoUrl={logoUrl} name={systemName} compact={collapsed} />
                        </Link>
                        {canToggle && !collapsed && (
                            <button
                                type="button"
                                onClick={onToggleCollapsed}
                                aria-label={expandCollapseLabel}
                                aria-expanded
                                title={expandCollapseLabel}
                                className={cn(
                                    'absolute end-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-glass-nav hover:text-foreground',
                                    focusRing
                                )}
                            >
                                <ToggleIcon
                                    aria-hidden="true"
                                    className="size-4 rtl:-scale-x-100"
                                />
                            </button>
                        )}
                    </div>

                    <nav className="min-h-0 flex-1 overflow-y-auto p-2">
                        <ul className="m-0 list-none space-y-0.5 p-0">
                            {items.map(item =>
                                renderRow(
                                    item.path,
                                    item.icon,
                                    t(item.labelKey),
                                    { to: item.path },
                                    isActive(item.path)
                                )
                            )}
                        </ul>
                    </nav>

                    <div className="shrink-0 border-t border-border-subtle p-2">
                        <ul className="m-0 list-none space-y-0.5 p-0">
                            {canToggle &&
                                collapsed &&
                                renderRow('toggle', ToggleIcon, expandCollapseLabel, {
                                    onClick: onToggleCollapsed,
                                })}
                            {renderRow(
                                'theme',
                                isDark ? Sun : Moon,
                                isDark ? t('lightMode') : t('darkMode'),
                                { onClick: () => setMode(isDark ? 'light' : 'dark') }
                            )}
                            <li className={cn(collapsed ? 'flex justify-center' : 'px-0.5')}>
                                <LanguageSelectorDropdown
                                    compact={collapsed}
                                    className={languageTriggerClass}
                                />
                            </li>
                            {renderRow('logout', LogOut, t('signOut'), {
                                onClick: () => {
                                    logout();
                                    navigate('/auth/login');
                                },
                            })}
                        </ul>
                    </div>
                </aside>
            </div>
        </TooltipProvider>
    );
};

export default TradingSidebar;
